import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { ExclamationTriangle, X } from "react-bootstrap-icons";
import { cn } from "@utils";

// Initials from the first and last word — "Jean Labuschagne" → "JL".
const initialsOf = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .filter((_, i, arr) => i === 0 || i === arr.length - 1)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");

// The shared "team view" as an overlapping stack of coloured initials avatars — each in that
// calendar's own colour, so an avatar matches the owner stripe on the grid. Hover to remove a
// colleague. Past five, the rest collapse into a "+N" avatar that opens a list of who they are.
const MAX_VISIBLE = 5;

const CalendarLegend = ({ entries, onRemove }) => {
  if (!entries.length) return null;

  const visible = entries.slice(0, MAX_VISIBLE);
  const hidden = entries.slice(MAX_VISIBLE);

  return (
    <div className="flex items-center -space-x-1">
      {visible.map((entry) => {
        const who = entry.isYou ? `${entry.name} (you)` : entry.name;
        const tip = entry.isError
          ? `${who} · couldn't load`
          : `${who} · ${entry.count} event${entry.count === 1 ? "" : "s"} in view`;
        return (
          <div key={entry.userId} className="group relative transition-[z-index] hover:z-10">
            <span
              title={tip}
              className={cn(
                "flex size-7 items-center justify-center rounded-full text-xs font-semibold text-white ring-2 ring-white",
                entry.swatchClass ?? "bg-zinc-400",
                entry.isLoading && "animate-pulse",
                entry.isError && "opacity-60",
              )}
            >
              {entry.isError ? (
                <ExclamationTriangle className="size-3.5" />
              ) : (
                initialsOf(entry.name)
              )}
            </span>
            {!entry.isYou && onRemove && (
              <button
                type="button"
                onClick={() => onRemove(entry.userId)}
                aria-label={`Remove ${entry.name}`}
                className="absolute -right-1 -top-1 hidden size-4 items-center justify-center rounded-full bg-zinc-700 text-white ring-2 ring-white hover:bg-zinc-900 group-hover:flex"
              >
                <X className="size-3" />
              </button>
            )}
          </div>
        );
      })}

      {hidden.length > 0 && (
        <Popover className="relative hover:z-10">
          <PopoverButton
            aria-label={`Show ${hidden.length} more`}
            className="flex size-7 items-center justify-center rounded-full bg-zinc-200 text-xs font-semibold text-zinc-600 ring-2 ring-white transition-colors hover:bg-zinc-300 focus:outline-none"
          >
            +{hidden.length}
          </PopoverButton>
          <PopoverPanel className="absolute left-0 z-30 mt-2 w-56 rounded-md bg-white p-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
            {hidden.map((entry) => (
              <div
                key={entry.userId}
                className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-zinc-50"
              >
                <span
                  className={cn(
                    "flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white",
                    entry.swatchClass ?? "bg-zinc-400",
                  )}
                >
                  {initialsOf(entry.name)}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-zinc-800">
                  {entry.isYou ? `${entry.name} (you)` : entry.name}
                </span>
                {entry.isError ? (
                  <ExclamationTriangle
                    title="Couldn't load this calendar"
                    className="size-3.5 text-yellow-500"
                  />
                ) : (
                  <span className="text-xs text-zinc-400">{entry.count}</span>
                )}
                {!entry.isYou && onRemove && (
                  <button
                    type="button"
                    onClick={() => onRemove(entry.userId)}
                    aria-label={`Remove ${entry.name}`}
                    className="flex size-4 shrink-0 items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-200 hover:text-zinc-600"
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </div>
            ))}
          </PopoverPanel>
        </Popover>
      )}
    </div>
  );
};

export default CalendarLegend;
